import React, { useState } from "react";
import { Copy, Check, Share2 } from "lucide-react";
import { useNotification } from "../../../shared/contexts/NotificationContext";
import { NotificationType } from "../../../../constants/Enums";
import { shareDineInInviteLink } from "../../../../services/helpers/DineInShareHelper";
import { DineInSessionMember } from "../../../../interfaces/DineInObject";
import DineInMemberList from "./DineInMemberList";
import "./styles/DineInSessionHeader.css";

interface DineInSessionHeaderProps {
  businessName: string;
  tableNumber: number;
  members: DineInSessionMember[];
  /** DineInSession.creatorId, threaded through so DineInMemberList can badge
   *  whoever opened the table as "Host". */
  creatorId: string;
  currentUserId?: string;
  billTotal: number;
  /** Shown highlighted in the header so anyone at the table can read it off
   *  to a late arrival without the host needing to remember/re-find it. */
  sessionCode: string;
}

const DineInSessionHeader: React.FC<DineInSessionHeaderProps> = ({
  businessName,
  tableNumber,
  members,
  creatorId,
  currentUserId,
  billTotal,
  sessionCode,
}) => {
  const { triggerNotification } = useNotification();
  const [copied, setCopied] = useState(false);

  // Same copy pattern as DineInCodeReveal's "Tap to copy" — this is the same
  // table code, just surfaced again here so it's always reachable once
  // you're past the one-time reveal screen.
  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(sessionCode);
      setCopied(true);
      triggerNotification("Table code copied!", NotificationType.SUCCESS);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      triggerNotification("Couldn't copy the code.", NotificationType.ERROR);
    }
  };

  // Native share sheet (or clipboard fallback) with the whole join link —
  // whoever opens it skips DineInCodeEntry entirely (see DineIn.tsx).
  const handleShareLink = async () => {
    const result = await shareDineInInviteLink(sessionCode, businessName);
    if (result === "copied") {
      triggerNotification("Invite link copied!", NotificationType.SUCCESS);
    } else if (result === "error") {
      triggerNotification("Couldn't share the link.", NotificationType.ERROR);
    }
  };

  return (
    <div className="dine-in-session-header">
      <div className="dine-in-session-header__row">
        <div className="dine-in-session-header__title">
          <span className="dine-in-session-header__pip" />
          <span className="dine-in-session-header__title-text">
            Table {tableNumber} &middot; {businessName}
          </span>
        </div>
        <div className="dine-in-session-header__bill">₹{Math.round(billTotal)}</div>
      </div>
      <div className="dine-in-session-header__row2">
        <DineInMemberList members={members} creatorId={creatorId} currentUserId={currentUserId} />
        <div className="dine-in-session-header__code-group">
          <button
            type="button"
            className="dine-in-session-header__code"
            onClick={handleCopyCode}
            aria-label={`Table code ${sessionCode}, tap to copy`}
          >
            <span className="dine-in-session-header__code-label">Table code</span>
            {sessionCode}
            {copied ? (
              <Check size={12} strokeWidth={2.5} />
            ) : (
              <Copy size={12} strokeWidth={2.5} />
            )}
          </button>
          <button
            type="button"
            className="dine-in-session-header__share-btn"
            onClick={handleShareLink}
            aria-label="Share invite link for this table"
          >
            <Share2 size={13} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DineInSessionHeader;
